import React, { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { usePageMeta } from "../lib/usePageMeta";
import { trackGateEvent } from "../lib/gateAnalytics";
import { recordGateAcceptance } from "../components/consent/gateStorage";
import { GATE_BACKGROUND, ResearchConfirmationPanel } from "../components/consent/ResearchConfirmationPanel";

/**
 * `/research-eligibility` — Google-Ads landing page.
 *
 * Minimal, premium, single-screen qualification: brand logo (via
 * `Header.jsx`, which suppresses the AnnouncementBar and the primary
 * catalog nav on this route) → centered headline → single qualification
 * question → two prominent buttons. The Research-Use fine print sits
 * at the bottom in small-but-readable type.
 *
 * Behavior contracts preserved from earlier gate revisions
 * --------------------------------------------------------
 * * Yes → `/shop` with the existing 12-key ad-parameter allowlist
 *   forwarded (gclid / gbraid / wbraid / gad_source / gad_campaignid /
 *   gclsrc / utm_source / utm_medium / utm_campaign / utm_term /
 *   utm_content / utm_id) via `resolveShopHref()`. Supports
 *   `?next=<safe-relative-path>` for deep-link ad landing.
 * * No → leaves the site (`about:blank`, `rel="noopener noreferrer"`).
 * * Analytics: still fires `gate_view` on mount, `eligibility_yes` on
 *   Yes click, `eligibility_no` on No click (both to `gtag` and
 *   `dataLayer`). First-/last-touch attribution is captured by the
 *   global `AnalyticsLoader` on landing — this component does not
 *   duplicate that mechanism.
 * * Yes copy is NOT worded as an age or laboratory-use confirmation,
 *   per the current spec — the single word "Yes" is enough. The legal
 *   restrictions are disclosed by the fine-print block below (which
 *   remains the authoritative Research-Use-Only surface).
 */

const AD_PARAM_ALLOWLIST = [
  "gclid",
  "gbraid",
  "wbraid",
  "gad_source",
  "gad_campaignid",
  "gclsrc",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "utm_id",
];

// Same-origin catalog slugs (kebab-case, 1–80 chars, no leading/trailing hyphen).
const SAFE_SLUG_RE = /^[a-z0-9](?:[a-z0-9-]{0,78}[a-z0-9])?$/;
// Opinly post slugs can run longer than catalog slugs.
const BLOG_SLUG_RE = /^[a-z0-9](?:[a-z0-9-]{0,198}[a-z0-9])?$/;

function isAllowedRelativePath(path) {
  if (typeof path !== "string" || path.length === 0) return false;
  if (path === "/shop") return true;
  if (path === "/categories") return true;
  const productMatch = path.match(/^\/product\/([^/]+)$/);
  if (productMatch) return SAFE_SLUG_RE.test(productMatch[1]);
  const categoryMatch = path.match(/^\/category\/([^/]+)$/);
  if (categoryMatch) return SAFE_SLUG_RE.test(categoryMatch[1]);
  const shopCatMatch = path.match(/^\/shop\/category\/([^/]+)$/);
  if (shopCatMatch) return SAFE_SLUG_RE.test(shopCatMatch[1]);
  if (path === "/blog") return true;
  const blogMatch = path.match(/^\/blog\/([^/]+)$/);
  if (blogMatch) return BLOG_SLUG_RE.test(blogMatch[1]);
  return false;
}

function looksMalicious(raw) {
  if (typeof raw !== "string" || raw.length === 0) return true;
  if (!raw.startsWith("/")) return true;
  if (raw.startsWith("//")) return true;
  if (raw.startsWith("/\\")) return true;
  if (/^\/%2f/i.test(raw)) return true;
  if (raw.indexOf("\\") !== -1) return true;
  // eslint-disable-next-line no-control-regex
  if (/[\x00-\x1f\x7f]/.test(raw)) return true;
  if (raw.indexOf("..") !== -1) return true;
  if (/\/\//.test(raw)) return true;
  if (raw.indexOf("@") !== -1) return true;
  return false;
}

function pickAdParams(searchString) {
  const src = new URLSearchParams(searchString);
  const out = new URLSearchParams();
  for (const key of AD_PARAM_ALLOWLIST) {
    const v = src.get(key);
    if (v) out.set(key, v);
  }
  return out;
}

/**
 * Compose the Yes-CTA destination — validates `?next=<safe path>`
 * against an allowlist, merges ad params into the destination query
 * (ad params always win on collisions), and falls back to `/shop` when
 * `next` is missing/invalid. Never returns a URL that carries an
 * absolute origin.
 */
export function resolveShopHref(searchString) {
  const outerParams = new URLSearchParams(searchString || "");
  const adParams = pickAdParams(searchString || "");

  const rawNext = outerParams.get("next");
  let basePath = "/shop";
  let mergedParams = new URLSearchParams();

  if (rawNext && !looksMalicious(rawNext)) {
    const qIdx = rawNext.indexOf("?");
    const path = qIdx === -1 ? rawNext : rawNext.slice(0, qIdx);
    const nextQs = qIdx === -1 ? "" : rawNext.slice(qIdx + 1);
    if (isAllowedRelativePath(path)) {
      basePath = path;
      const nextParams = new URLSearchParams(nextQs);
      for (const [k, v] of nextParams.entries()) {
        if (!AD_PARAM_ALLOWLIST.includes(k)) mergedParams.set(k, v);
      }
    }
  }

  for (const [k, v] of adParams.entries()) mergedParams.set(k, v);
  const qs = mergedParams.toString();
  return qs ? `${basePath}?${qs}` : basePath;
}

export default function ResearchEligibilityGate() {
  const location = useLocation();
  const shopHref = useMemo(() => resolveShopHref(location.search), [location.search]);

  usePageMeta({
    title: "Research Eligibility",
    description:
      "Confirm research-use eligibility before browsing Medical Peptide Store — a laboratory reference-standard catalog for qualified research use only.",
    canonicalPath: "/research-eligibility",
    ogType: "website",
    robots: "noindex, follow",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "Research Eligibility — Medical Peptide Store",
        "url": "https://medicalpeptidestore.com/research-eligibility",
        "description":
          "Research-eligibility landing for Medical Peptide Store. Every visitor sees this page identically; no cloaking, no auto-redirect.",
        "inLanguage": "en-US",
        "isPartOf": { "@id": "https://medicalpeptidestore.com/#website" },
        "about": { "@id": "https://medicalpeptidestore.com/#organization" },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home",
            "item": "https://medicalpeptidestore.com/" },
          { "@type": "ListItem", "position": 2, "name": "Research Eligibility",
            "item": "https://medicalpeptidestore.com/research-eligibility" },
        ],
      },
    ],
  });

  useEffect(() => {
    trackGateEvent("gate_view", { page_path: "/research-eligibility" });
  }, []);

  const trackYes = () => {
    // Persist first so a failing analytics call can never drop the acceptance.
    recordGateAcceptance();
    trackGateEvent("eligibility_yes");
  };

  return (
    <div
      className="min-h-[70vh] flex flex-col justify-between px-6 pb-8 pt-8 sm:pt-10 text-white"
      style={{ backgroundImage: GATE_BACKGROUND }}
      data-testid="research-eligibility-page"
    >
      <ResearchConfirmationPanel
        yesTo={shopHref}
        onYes={trackYes}
        onNo={() => trackGateEvent("eligibility_no")}
      />
    </div>
  );
}
